'use client'

import { useRef, useState } from 'react'
import clsx from 'clsx'
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'

import useGooglePlacesInit from 'lib/googleApis/useGooglePlacesInit'
import { AddressToDisplayInterface } from 'shared/types/forms'
import TextField from 'shared/ui/form/TextField'
import { Listbox, ListboxItem } from 'shared/ui/Listbox'
import type { ListboxProps } from 'shared/ui/Listbox/Listbox'
import type { ListboxItemProps } from 'shared/ui/Listbox/ListboxItem'
import type { PopoverProps } from 'shared/ui/Popover'
import { Popover } from 'shared/ui/Popover'
import { ZIndex } from 'shared/ui/types'
import { addTestId } from 'shared/utils/tests'

import styles from './AddressAutocompleteInput.module.scss'

interface HandleSelectProp {
  description: string
}
interface TempPayload {
  administrative_area_level_1?: string
  administrative_area_level_2?: string
  country?: string
  locality?: string
  postal_code?: string
  route?: string
  street_number?: string
}

interface GoogleAddressInputProps {
  handleUpdateForm: (payload: AddressToDisplayInterface) => void
  prepareFullAddress: () => string
}

interface AddressComponents {
  long_name: string
  short_name: string
  types: string[]
}

interface Results {
  address_components: AddressComponents[]
  formatted_address: string
  place_id: string
  plus_code: { compound_code: string; global_code: string }
  types: string[]
}

interface MatchedSubstrings {
  length: number
  offset: number
}

interface StructuredFormatting {
  main_text: string
  main_text_matched_substrings: MatchedSubstrings[]
  secondary_text: string
}

interface Terms {
  offset: number
  value: string
}

interface Suggestion {
  description: string
  matched_substrings: MatchedSubstrings[]
  length: number
  place_id: string
  reference: string
  structured_formatting: StructuredFormatting
  terms: Terms[]
  types: string[]
}

const GoogleAddressInput = ({
  handleUpdateForm,
  prepareFullAddress
}: GoogleAddressInputProps) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    initOnMount: true,
    requestOptions: {
      language: 'pl',
      locationRestriction: {
        north: 54.861845338996794,
        south: 48.984115281753475,
        west: 14.113152947026498,
        east: 24.193062904956705
      }
    },
    debounce: 300
  })

  const anchorRef = useRef<HTMLDivElement>(null)

  const [label, setLabel] = useState<string>(
    prepareFullAddress() || 'Wyszukaj adres'
  )
  let payload = {
    city: '',
    postalCode: '',
    street: '',
    houseNumber: ''
  }
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLabel('Wyszukaj adres')
    setValue(e.target.value)
  }

  const handleSelect =
    ({ description }: HandleSelectProp) =>
    () => {
      setValue(description, false)
      clearSuggestions()

      getGeocode({ address: description })
        .then((results: Results[]) => {
          const summary: Results = results[0]
          let tempPayload: TempPayload = {
            administrative_area_level_1: '',
            administrative_area_level_2: '',
            country: '',
            locality: '',
            postal_code: '',
            route: '',
            street_number: ''
          }

          summary.address_components.forEach(el => {
            tempPayload = {
              ...tempPayload,
              [el.types[0]]: el.long_name
            }
          })

          const { postal_code } = tempPayload
          const postalCode =
            postal_code && postal_code.length <= 5 ? '' : postal_code

          payload = {
            city: tempPayload.locality || '',
            postalCode: postalCode || '',
            street: tempPayload.route || '',
            houseNumber: tempPayload.street_number || ''
          }

          handleUpdateForm(payload)
        })
        .catch(err => {
          console.error(err)
        })
    }

  const renderSuggestions = () =>
    data.map((suggestion: Suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text }
      } = suggestion

      return (
        <SuggestionsOption
          key={place_id}
          optionId={place_id}
          role='option'
          tabIndex={0}
        >
          <strong>{main_text}</strong> <small>{secondary_text}</small>
        </SuggestionsOption>
      )
    })

  const handleOnSelect = (id: string) => {
    const suggestion = data?.find((sug: Suggestion) => sug.place_id === id) as
      | Suggestion
      | undefined

    if (suggestion) {
      handleSelect(suggestion)()
    }
  }

  return (
    <>
      <div className={styles.suggestionsWrapper}>
        <TextField
          ref={
            anchorRef as React.RefObject<HTMLInputElement | HTMLTextAreaElement>
          }
          value={value}
          onChange={handleChange}
          onMouseDown={() => setLabel('Wyszukaj adres')}
          disabled={!ready}
          placeholder={prepareFullAddress() || 'wybierz z listy po wpisaniu'}
          label={label}
          inputProps={{
            ...addTestId('search-address-input'),
            'aria-autocomplete': 'list',
            'aria-controls': 'suggestions-list',
            'aria-expanded': status === 'OK',
            'aria-activedescendant': value ? `suggestion-${value}` : undefined
          }}
        />
      </div>

      <StyledPopoverPaper
        $width={anchorRef.current?.clientWidth || 0}
        id='suggestions-list'
        anchorEl={anchorRef}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        open={status === 'OK'}
        onClose={clearSuggestions}
        zIndex={ZIndex.POPOVER}
        distanceFromAnchor={0}
      >
        <SuggestionsList
          id='suggestions-list'
          selectedOptionId={value}
          onSelect={handleOnSelect}
        >
          {renderSuggestions()}
        </SuggestionsList>
      </StyledPopoverPaper>
    </>
  )
}

const AddressAutocompleteInputSwitch = (props: GoogleAddressInputProps) => {
  const { isLoaded } = useGooglePlacesInit()

  return isLoaded ? (
    <GoogleAddressInput {...props} />
  ) : (
    <div className={styles.suggestionsWrapper}>
      <TextField
        value={''}
        disabled={true}
        placeholder={'Wczytywanie...'}
        label={'Wczytywanie...'}
      />
    </div>
  )
}

export default AddressAutocompleteInputSwitch

const StyledPopoverPaper = ({
  $width,
  className,
  style,
  ...props
}: PopoverProps & { $width: number }) => (
  <Popover
    {...props}
    className={clsx(styles.popoverPaper, className)}
    style={
      {
        '--popover-min-width': `${$width}px`,
        ...style
      } as React.CSSProperties
    }
  />
)

export const SuggestionsList = ({ className, ...props }: ListboxProps) => (
  <Listbox {...props} className={clsx(styles.list, className)} />
)

export const SuggestionsOption = ({
  className,
  ...props
}: ListboxItemProps) => (
  <ListboxItem {...props} className={clsx(styles.option, className)} />
)
